import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CommonRootState } from '@common/types/common-root-state-type';
import { createSelector } from '@reduxjs/toolkit';
import { isExtensionContext } from '@common/services/detect-context';
import Bowser from 'bowser';
import { sessionSelectOneObjectByQuery } from './DBServiceThunks';
import { NotifierConnectionStatus } from '@/common/noifier-module/types';

export interface ConnectedSocket {
  id: string;
  user?: {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  device: string;
  browserType: string;
  connectionTime: string;
  connectedFrom: string;
  domain: string;
}

interface WhatsAppEngine {
  id: string;
  type: 'whatsapp';
  name: string;
  domain: string;
  connectionTime: string;
}

type Engine = ConnectedSocket | WhatsAppEngine;

interface DeliveryStatus {
  color: string;
  text: string;
  alert: {
    type: 'error' | 'warning';
    message: string;
  } | null;
}

export interface ConnectedEnginesState {
  sockets: ConnectedSocket[];
}

interface ConnectedEnginesDisplayState {
  engines: Engine[];
  status: DeliveryStatus;
  hasGateway: boolean;
  hasBrowser: boolean;
  isNotifierConnected: boolean;
}

const initialState: ConnectedEnginesState = {
  sockets: []
};

export const connectedEnginesSlice = createSlice({
  name: 'connectedEngines',
  initialState,
  reducers: {
    updateConnectedSockets: (state, action: PayloadAction<ConnectedSocket[]>) => {
      state.sockets = action.payload;
    },
    setAllClients: (state, action: PayloadAction<ConnectedSocket[]>) => {
      state.sockets = action.payload;
    },
    upsertClient: (state, action: PayloadAction<ConnectedSocket>) => {
      const index = state.sockets.findIndex(socket => socket.id === action.payload.id);
      if (index !== -1) {
        state.sockets[index] = action.payload;
      } else {
        state.sockets.push(action.payload);
      }
    },
    removeClient: (state, action: PayloadAction<{ clientId: string }>) => {
      state.sockets = state.sockets.filter(socket => socket.id !== action.payload.clientId);
    }
  }
});

// Selectors
export const selectConnectedSockets = (state: CommonRootState) => 
  state.ConnectedEnginesReducer?.sockets || [];

export const { updateConnectedSockets, setAllClients, upsertClient, removeClient } = connectedEnginesSlice.actions;

export default connectedEnginesSlice.reducer;

// Unified selector that handles all engine detection and status logic
export const selectConnectedEnginesState = createSelector(
  [
    selectConnectedSockets,
    (state: any) => state.NotifierReducer?.connectionStatus,
    (state: any) => sessionSelectOneObjectByQuery(state.DBSessionReducer || {}, {
      status: { $in: ["connected"] }
    })
  ],
  (connectedSockets, connectionStatus, activeSession): ConnectedEnginesDisplayState => {
    // Helper function to check if notifier is still valid
    const isNotifierConnected = connectionStatus === NotifierConnectionStatus.AUTHENTICATED;
    
    // Check if we're currently on WhatsApp Web - if extension context, we're definitely on WhatsApp Web
    const isCurrentlyOnWhatsAppWeb = (typeof window !== 'undefined' && window.location.hostname === 'web.whatsapp.com');
    
    // Filter and validate sockets - only accept our new client types
    const validSockets = connectedSockets.filter(socket => {
      if (!socket) return false;
      
      // Must have all required fields
      if (!socket.domain || !socket.connectedFrom || !socket.browserType) {
        return false;
      }
      
      // Accept our new client types: WhatsApp Web, Gateway, or Website
      return ['WhatsApp Web', 'Gateway', 'Website'].includes(socket.browserType);
    });

    // Add current extension as a socket if we're in extension context
    const currentExtensionSocket: ConnectedSocket | null = isExtensionContext() ? {
      id: 'current-extension',
      device: 'Desktop',
      browserType: Bowser.getParser(window.navigator.userAgent).getBrowser().name || 'Unknown',
      connectionTime: new Date().toISOString(),
      connectedFrom: 'webapp',
      domain: window.location.hostname === 'web.whatsapp.com' ? 'web.whatsapp.com' : 'website'
    } : null;

    // Combine all engines
    const allEngines: Engine[] = [
      ...(currentExtensionSocket ? [currentExtensionSocket] : []),
      ...validSockets,
      ...(activeSession ? [{
        id: activeSession._id,
        type: 'whatsapp' as const,
        name: 'WhatsApp Server',
        domain: 'gateway',
        connectionTime: activeSession.updatedAt?.toString() || new Date().toString()
      }] : [])
    ];

    // Determine engine types
    const hasGateway = activeSession !== null || allEngines.some(engine => 
      ('browserType' in engine && engine.browserType === 'Gateway') ||
      ('type' in engine && engine.type === 'whatsapp')
    );
    
    const hasWhatsAppWeb = isCurrentlyOnWhatsAppWeb || allEngines.some(engine => 
      'browserType' in engine && engine.browserType === 'WhatsApp Web'
    );
    
    const hasBrowser = currentExtensionSocket !== null || 
      allEngines.some(engine => 
        'browserType' in engine && ['WhatsApp Web', 'Website'].includes(engine.browserType)
      );

    // Calculate delivery status directly (not as a function)
    let status: DeliveryStatus;
    
    if (!isNotifierConnected) {
      status = {
        color: '#ef4444',
        text: 'Not Connected',
        alert: {
          type: 'error',
          message: 'The notification service is not connected. Messages may not be delivered.'
        }
      };
    } else if (hasGateway) {
      status = {
        color: '#22c55e',
        text: 'WhatsApp Ready (Offline OK)',
        alert: null
      };
    } else if (hasWhatsAppWeb || isCurrentlyOnWhatsAppWeb) {
      status = {
        color: '#f97316',
        text: 'WhatsApp Web Only',
        alert: {
          type: 'warning',
          message: 'Keep WhatsApp Web open in your browser for messages to be sent.'
        }
      };
    } else {
      status = {
        color: '#ef4444',
        text: 'No WhatsApp Connected',
        alert: {
          type: 'error',
          message: 'Messages cannot be sent. Connect WhatsApp to enable message delivery.'
        }
      };
    }

    return {
      engines: allEngines,
      status,
      hasGateway,
      hasBrowser,
      isNotifierConnected
    };
  }
); 